import { createFileRoute } from "@tanstack/react-router";
import QRCode from "qrcode";
import { useEffect, useState, type FormEvent } from "react";
import { Banner, Button, Card, Field, Input, PageHeader } from "@magarac/ui";
import { CalendarConnectCard } from "../../components/CalendarConnectCard";
import { CalendarFeedCard } from "../../components/CalendarFeedCard";
import { HelpIcon } from "../../components/HelpIcon";
import { authClient } from "../../lib/auth-client";

export const Route = createFileRoute("/_app/account")({
  component: AccountPage,
});

/** The secret from an otpauth:// URI, for people who type it into their app instead of scanning. */
function secretOf(uri: string): string {
  return (new URL(uri).searchParams.get("secret") ?? "").replace(/(.{4})/g, "$1 ").trim();
}

function authError(err: { message?: string; status?: number } | null | undefined): string {
  if (!err) return "Something went wrong. Please try again.";
  if (err.status === 429) return "Too many tries. Wait a minute, then try again.";
  const m = (err.message ?? "").toLowerCase();
  if (m.includes("password")) return "That password is not right.";
  if (m.includes("code") || m.includes("otp") || m.includes("totp"))
    return "That code did not work. Codes change every 30 seconds; type the newest one.";
  return err.message || "Something went wrong. Please try again.";
}

/** ACC-6: two-step sign-in with an authenticator app. Screen key: account */
function AccountPage() {
  const session = authClient.useSession();
  const user = session.data?.user as
    { name?: string | null; email: string; twoFactorEnabled?: boolean | null } | undefined;
  const [password, setPassword] = useState("");
  const [setup, setSetup] = useState<{ uri: string; backupCodes: string[] } | null>(null);
  const [qr, setQr] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [codes, setCodes] = useState<string[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    if (!setup) return;
    let live = true;
    void QRCode.toDataURL(setup.uri, { margin: 1, width: 220 }).then((url) => {
      if (live) setQr(url);
    });
    return () => {
      live = false;
    };
  }, [setup]);

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await fn();
    } finally {
      setBusy(false);
    }
  };

  const start = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      const { data, error: err } = await authClient.twoFactor.enable({
        method: "totp",
        ...(password ? { password } : {}),
      });
      if (err || !data || !("totpURI" in data)) return setError(authError(err));
      setSetup({ uri: data.totpURI, backupCodes: data.backupCodes });
      setPassword("");
    });
  };

  const confirm = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      const { error: err } = await authClient.twoFactor.verifyTotp({ code: code.trim() });
      if (err) return setError(authError(err));
      setCodes(setup?.backupCodes ?? null);
      setSetup(null);
      setCode("");
      setNotice("Two-step sign-in is on. Save your backup codes somewhere safe.");
      await session.refetch();
    });
  };

  const turnOff = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      const { error: err } = await authClient.twoFactor.disable({
        ...(password ? { password } : {}),
      });
      if (err) return setError(authError(err));
      setPassword("");
      setCodes(null);
      setNotice("Two-step sign-in is off.");
      await session.refetch();
    });
  };

  const newCodes = () =>
    void run(async () => {
      const { data, error: err } = await authClient.twoFactor.generateBackupCodes({
        ...(password ? { password } : {}),
      });
      if (err || !data) return setError(authError(err));
      setCodes(data.backupCodes);
      setPassword("");
      setNotice("New backup codes made. The old ones no longer work.");
    });

  const on = Boolean(user?.twoFactorEnabled);
  const passwordField = (
    <Field
      id="acct-password"
      label="Your password"
      hint="Leave blank if you sign in with an email link or Google."
    >
      <Input
        id="acct-password"
        type="password"
        autoComplete="current-password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
      />
    </Field>
  );

  return (
    <div className="mx-auto max-w-xl">
      <PageHeader title="Your sign-in" help={<HelpIcon screen="account" />} />
      <Card className="mb-6">
        <p className="font-medium">{user?.name ?? user?.email}</p>
        <p className="text-sm text-muted">{user?.email}</p>
      </Card>
      <Card title="Two-step sign-in">
        {error ? <Banner tone="danger">{error}</Banner> : null}
        {notice ? <Banner tone="ok">{notice}</Banner> : null}
        {setup ? (
          <form onSubmit={confirm}>
            <ol className="mb-4 list-decimal space-y-2 pl-5">
              <li>
                Open an authenticator app (Google Authenticator, Microsoft Authenticator,
                1Password).
              </li>
              <li>Scan this code, or type the key below.</li>
              <li>Type the 6-digit code the app shows.</li>
            </ol>
            {qr ? (
              <img
                src={qr}
                alt="QR code for your authenticator app"
                className="mb-2 h-[220px] w-[220px]"
              />
            ) : null}
            <p className="mb-4 font-mono text-sm">{secretOf(setup.uri)}</p>
            <Field id="acct-code" label="6-digit code">
              <Input
                id="acct-code"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
            </Field>
            <div className="flex gap-2">
              <Button type="submit" busy={busy} disabled={code.trim().length < 6}>
                Turn on
              </Button>
              <Button variant="secondary" onClick={() => setSetup(null)}>
                Cancel
              </Button>
            </div>
          </form>
        ) : on ? (
          <form onSubmit={turnOff}>
            <p className="mb-4">
              On. When you sign in with your password, J360 also asks for a code from your
              authenticator app.
            </p>
            {passwordField}
            <div className="flex flex-wrap gap-2">
              <Button type="submit" variant="danger" busy={busy}>
                Turn off
              </Button>
              <Button variant="secondary" busy={busy} onClick={newCodes}>
                Make new backup codes
              </Button>
            </div>
          </form>
        ) : (
          <form onSubmit={start}>
            <p className="mb-4">
              Off. Turn it on so a stolen password alone cannot get into your business: signing in
              will also need a code from an app on your phone.
            </p>
            {passwordField}
            <Button type="submit" busy={busy}>
              Set up two-step sign-in
            </Button>
          </form>
        )}
        {codes ? (
          <div className="mt-4 rounded-lg bg-canvas p-3">
            <p className="mb-2 font-semibold">Backup codes</p>
            <p className="mb-2 text-sm text-muted">
              Each works once if you lose your phone. Keep them somewhere safe, like a password
              manager. You will not see them again.
            </p>
            <ul className="grid grid-cols-2 gap-1 font-mono text-sm">
              {codes.map((c) => (
                <li key={c}>{c}</li>
              ))}
            </ul>
          </div>
        ) : null}
      </Card>
      <CalendarConnectCard resourceId="me" back="/account" />
      <CalendarFeedCard resourceId="me" />
    </div>
  );
}
